import React, { useCallback } from 'react';
import {
  classes,
  getDirection,
  IconButton,
  isNumber,
  useSlug,
} from '@bloomberg/media-ui-core';
import { GlobalProps } from '@bloomberg/media-ui-core/types';
import { ExitThin } from '@bloomberg/media-ui-icons';

import { Change } from '../Change';
import { SizedPrice } from '../SizedPrice';
import { useTranslateMessage } from '../utils/translate-message';

import styles from './SecurityPill.module.css';

export enum Size {
  small = 'small',
  default = 'default',
}

export enum ChangeSize {
  'xsmall' = 'xsmall',
  'small' = 'small',
  'default' = 'default',
}

export enum Lines {
  'one' = 'one',
  'two' = 'two',
  'three' = 'three',
}

export enum Variant {
  'comparison' = 'comparison',
  'refresh2024' = 'refresh2024',
  'refreshbar2024' = 'refreshbar2024',
  'default' = 'default',
}

export const SECURITY_PILL_CLASSNAMES: Record<Lines, string> = {
  [Lines.one]: 'oneLine',
  [Lines.two]: 'twoLines',
  [Lines.three]: 'threeLines',
};

export type ComparisonPill = {
  variant: Variant.comparison;
  color: string;
  lines?: Lines.one | Lines.two;
  onDismiss?: (ticker: string) => void;
};

export type RefreshPill = {
  variant: Variant.refresh2024 | Variant.refreshbar2024;
  lines?: Lines.one | Lines.two | Lines.three;
  price?: number | null;
  currency?: string;
  showCurrency?: boolean;
};

export type DefaultPill = {
  variant: Variant.default;
  lines?: Lines.one | Lines.two;
};

export type SecurityPillProps = {
  displayName?: string;
  fullWidth?: boolean;
  isPrivate?: boolean;
  percentChange?: number | null;
  size?: Size.small | Size.default;
  ticker: string;
  onClick?: (ticker: string) => void;
} & (ComparisonPill | RefreshPill | DefaultPill) &
  GlobalProps;

export const ChangeSizeMap: Record<string, ChangeSize> = {
  [Variant.comparison]: ChangeSize.default,
  [Variant.refreshbar2024]: ChangeSize.small,
  default: ChangeSize.default,
  [Size.small]: ChangeSize.small,
};

export const SecurityPill = React.forwardRef<HTMLDivElement, SecurityPillProps>(
  function SecurityPillComponent(props: SecurityPillProps, ref) {
    const {
      displayName,
      fullWidth,
      isPrivate,
      lines,
      name = 'security-pill',
      percentChange,
      size = Size.default,
      ticker,
      onClick,
      variant = Variant.default,
    } = props;

    const translate = useTranslateMessage();
    const privateCompanyLabel = translate({
      id: 'media-ui-markets-private-company',
      defaultMessage: 'Private company',
    });

    const isMissingPercentChange = !isNumber(percentChange);
    const componentName = useSlug(name);
    const direction = getDirection(percentChange || 0);
    const changeSize = ChangeSizeMap[size] || ChangeSizeMap[variant];
    const sizedPriceSize = size || Size.default;

    const isRefreshPill =
      variant === Variant.refresh2024 || variant === Variant.refreshbar2024;
    const isComparisonPill = variant === Variant.comparison;

    const price = isRefreshPill ? (props as RefreshPill).price : null;
    const isMissingPrice = !isNumber(price);
    let currency = undefined;

    if (isRefreshPill && !isMissingPrice) {
      const { showCurrency, currency: refreshCurrency } = props as RefreshPill;

      if (showCurrency) {
        currency = refreshCurrency;
      }
    }

    const onDismiss = isComparisonPill
      ? (props as ComparisonPill).onDismiss
      : () => {};

    const title = isRefreshPill ? displayName || ticker : '';

    const handleOnClick = useCallback(() => {
      onClick?.(ticker);
    }, [onClick, ticker]);

    const cx = classes(
      [
        styles.securityPill,
        fullWidth && styles.fullWidth,
        !isComparisonPill && styles[direction],
        styles[variant],
        lines && styles[SECURITY_PILL_CLASSNAMES[lines]],
        { [styles[`size-${size}`]]: Boolean(size) },
      ],
      {
        ...props,
        name,
      }
    );

    return (
      <div
        ref={ref}
        data-component={componentName}
        className={cx}
        onClick={handleOnClick}
        title={title}
      >
        {isComparisonPill && (
          <span
            className={classes([styles.tickerColorBox])}
            style={{ backgroundColor: (props as ComparisonPill).color }}
          />
        )}
        <span className={styles.ticker}>{displayName || ticker}</span>
        <div
          className={classes([
            styles.priceWrapper,
            !isMissingPercentChange && styles.priceChange,
          ])}
        >
          {isPrivate && isMissingPrice && isMissingPercentChange && (
            <b className={classes([styles.privateCompany])}>
              {privateCompanyLabel}
            </b>
          )}

          {!isMissingPrice && (
            <SizedPrice
              size={sizedPriceSize}
              isDark={isRefreshPill}
              className={styles.price}
              price={price}
              currency={currency}
            />
          )}
          {!isMissingPercentChange && (
            <Change
              isDark={isRefreshPill}
              variant={isRefreshPill ? Variant.refresh2024 : Variant.default}
              percentChange={percentChange}
              percentChangeClassName={styles.percentChange}
              marketArrowClassName={styles.marketArrow}
              hideMarketArrow={isComparisonPill}
              size={changeSize}
            />
          )}
          {!isPrivate && isMissingPercentChange && (
            <b className={classes([styles.missingPricingData])}>--</b>
          )}
          {isComparisonPill && onDismiss && (
            <span>
              <IconButton
                type="button"
                size="tiny"
                onClick={() => onDismiss(ticker)}
              >
                <ExitThin />
              </IconButton>
            </span>
          )}
        </div>
      </div>
    );
  }
);
